import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, ArrowRight, ArrowDown, Plus, Minus } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Reveal, RevealLines } from '../components/Reveal';
import { FAMILIES, APPLICATIONS, WHY, TECHNOLOGY, PROCESS, PRODUCTS, ATMOSPHERE, HERO_HOME } from '../data/products';
import AtmoBg from '../components/AtmoBg';
import ChapterBreak from '../components/ChapterBreak';

// ---------- Feature product picks (curated signature range) ----------
const FEATURED_SLUGS = ['cs37', 'cs33', 'ss35', 'cs25'];
const featured = FEATURED_SLUGS.map((s) => PRODUCTS.find((p) => p.slug === s)).filter(Boolean);

// ---------- Why: three thematic pillars mapped to existing WHY items ----------
const WHY_PILLARS = [
  {
    id: 'design',
    label: 'Design',
    lead: 'Architecture that behaves like a product.',
    items: WHY.filter((w) => ['Premium Materials', 'Smart Ready', 'Sustainable'].includes(w.title)),
  },
  {
    id: 'engineering',
    label: 'Engineering',
    lead: 'Millimetre precision, not building tolerances.',
    items: WHY.filter((w) => ['Factory Built', 'Precision Engineered'].includes(w.title)),
  },
  {
    id: 'delivery',
    label: 'Delivery',
    lead: 'From line-off to move-in in weeks, not years.',
    items: WHY.filter((w) => ['Rapid Installation', 'Turnkey Delivery'].includes(w.title)),
  },
];

// ---------- Trust stats ----------
const TRUST = [
  { value: '±2mm', label: 'Structural tolerance', body: 'Automotive-grade precision on every panel and joint.' },
  { value: '90–120', label: 'Days to dispatch', body: 'Factory production time from order confirmation to logistics.' },
  { value: 'Global', label: 'Delivery footprint', body: 'Made in India. Shipped and installed worldwide.' },
  { value: '100%', label: 'Turnkey handover', body: 'Design, manufacture, transport and installation under one signature.' },
];

// ---------- Small helpers ----------
const SectionLabel = ({ n, children, tone = 'dark' }) => (
  <div className={`flex items-center gap-4 text-[11px] tracking-[0.32em] uppercase ${tone === 'light' ? 'text-white/60' : 'text-black/45'}`}>
    <span className={tone === 'light' ? 'text-[#C7A36A]' : 'text-[#C7A36A]'}>{n}</span>
    <span className={tone === 'light' ? 'w-10 border-t border-white/25' : 'w-10 border-t border-black/20'} />
    <span>{children}</span>
  </div>
);

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 160]);
  const heroScale = useTransform(scrollY, [0, 800], [1.02, 1.12]);
  const [whyTab, setWhyTab] = useState('design');
  const [openStep, setOpenStep] = useState(0);

  const activeWhy = WHY_PILLARS.find((p) => p.id === whyTab) || WHY_PILLARS[0];

  return (
    <main data-testid="home-page">
      <Helmet>
        <title>MODULAROOM | Luxury Modular Homes & Prefab Living</title>
        <meta name="description" content="MODULAROOM designs luxury modular homes, capsule suites and prefabricated homes for resorts, farmhouses and modern living. Factory-built, delivered turnkey from India." />
        <link rel="canonical" href="https://modularoom.com/" />
      </Helmet>

      {/* ============ 01 · HERO ============ */}
      <section className="relative h-[100svh] w-full overflow-hidden bg-black text-white">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO_HOME} alt="MODULAROOM CS37 luxury modular home in a vineyard at sunrise" className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-black/70" />
        <div className="relative z-10 h-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-36">
          <div className="text-[11px] tracking-[0.36em] uppercase text-[#C7A36A] mb-6">Modularoom</div>
          <RevealLines
            className="font-display text-[3.25rem] leading-[0.95] sm:text-7xl md:text-8xl lg:text-[8.5rem] font-medium tracking-tight max-w-[1200px]"
            lines={['Lifestyle-led', <span key="ll" className="italic font-normal">modular living.</span>]}
          />
          <Reveal delay={0.4} className="mt-10 max-w-lg text-base md:text-lg text-white/85 leading-relaxed font-light">
            Luxury modular spaces engineered with precision and designed for extraordinary living.
          </Reveal>
          <Reveal delay={0.6} className="mt-12 flex flex-wrap gap-3">
            <Link data-testid="hero-explore-btn" to="/products" className="group inline-flex items-center gap-2 rounded-full bg-white text-[#111] px-8 py-4 text-[13px] tracking-[0.18em] uppercase hover:bg-[#C7A36A] hover:text-white transition-colors duration-500">
              Explore Products <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link data-testid="hero-book-btn" to="/contact#book" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 text-[13px] tracking-[0.18em] uppercase hover:bg-white hover:text-[#111] transition-colors duration-500">
              Book Consultation
            </Link>
          </Reveal>
        </div>
        <div className="absolute bottom-6 right-6 md:bottom-10 md:right-12 flex items-center gap-3 text-[10px] tracking-[0.32em] uppercase text-white/65">
          <span>Scroll</span>
          <ArrowDown size={14} />
        </div>
      </section>

      {/* ============ 02 · BRAND PHILOSOPHY (slim) ============ */}
      <section className="relative overflow-hidden bg-white text-[#111] py-32 md:py-44">
        <AtmoBg src={ATMOSPHERE.brandLight} base="white" opacity={0.07} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <SectionLabel n="01">Brand Philosophy</SectionLabel>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-20 items-end">
            <Reveal className="md:col-span-8">
              <h2 className="font-display text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[6rem] leading-[0.98] tracking-tight">
                Engineered<br/>like a product.<br/>
                <span className="text-black/40 italic">Crafted for modern living.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="md:col-span-4">
              <p className="text-base text-black/65 leading-[1.75] max-w-md">
                This is more than modular construction. It is the future of space, designed as a product, built with precision and delivered with confidence.
              </p>
              <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase hover:text-[#C7A36A] transition-colors">Read the philosophy →</Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ EXPLORE MODULAROOM (nav preview) ============ */}
      <section className="bg-[#F5F5F5] text-[#111] py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-[10px] tracking-[0.32em] uppercase text-black/45 mb-10">Explore MODULAROOM</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 md:gap-y-10 gap-x-10 border-t border-black/10 pt-10">
            {[
              ['Collections','/collections','Two families. Seven signature models.'],
              ['Engineering','/engineering','Product tolerances. Product-grade assurance.'],
              ['Materials','/materials','A palette selected to age gracefully.'],
              ['Hospitality','/hospitality','Signature suites for resort operators.'],
              ['Residential','/residential','Modern residences delivered as products.'],
              ['Journal','/journal','Field notes and design thinking.'],
              ['Gallery','/gallery','Every render in one editorial spread.'],
              ['FAQ','/faq','What every high-ticket buyer wants to know.'],
            ].map(([t, to, d]) => (
              <Link key={t} to={to} className="group block">
                <div className="font-display text-2xl tracking-tight group-hover:text-[#C7A36A] transition-colors">{t}</div>
                <div className="mt-2 text-sm text-black/60 leading-relaxed">{d}</div>
                <div className="mt-4 text-[11px] tracking-[0.24em] uppercase text-black/40 group-hover:text-[#C7A36A] transition-colors">Explore →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 03 · FEATURED PRODUCTS ============ */}
      <section className="bg-white text-[#111] pb-40 md:pb-56">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 mb-16 md:mb-24">
          <SectionLabel n="02">Featured Products</SectionLabel>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <h2 className="md:col-span-8 font-display text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.98]">
              Two families.<br/><span className="text-black/40 italic">One obsession with detail.</span>
            </h2>
            <div className="md:col-span-4 text-sm md:text-[15px] text-black/65 leading-relaxed max-w-sm md:justify-self-end">
              Capsule Suites for statement architecture. Studio Suites for everyday modern living. Every model is a complete, finished product, delivered ready to inhabit.
            </div>
          </div>
        </div>

        {/* Family split — dramatic full-bleed alternating */}
        {[FAMILIES.capsule, FAMILIES.studio].map((f, i) => (
          <Link key={f.id} to="/products" data-testid={`family-${f.id}`}
            className={`group relative block w-full mb-16 md:mb-24 last:mb-0`}>
            <div className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-stretch ${i % 2 === 1 ? 'md:[direction:rtl]' : ''}`}>
              <div className="md:col-span-8 md:[direction:ltr]">
                <div className="relative aspect-[16/10] md:aspect-[16/9] overflow-hidden bg-[#F5F5F5]">
                  <img src={f.image} alt={f.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-out group-hover:scale-[1.045]" />
                </div>
              </div>
              <div className={`md:col-span-4 md:[direction:ltr] flex flex-col justify-center px-6 md:px-12`}>
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">{f.tagline}</div>
                <div className="font-display text-5xl md:text-6xl lg:text-7xl mt-4 tracking-tight leading-[0.98]">{f.name}.</div>
                <div className="mt-6 text-sm md:text-[15px] text-black/65 leading-relaxed max-w-md">{f.description}</div>
                <div className="mt-10 inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase group-hover:text-[#C7A36A] transition-colors">
                  Explore collection <ArrowUpRight size={16} />
                </div>
              </div>
            </div>
          </Link>
        ))}

        {/* Featured model row */}
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 mt-24 md:mt-32">
          <div className="flex items-end justify-between mb-10">
            <div className="text-[10px] tracking-[0.32em] uppercase text-black/45">The signature range</div>
            <Link to="/products" className="text-[12px] tracking-[0.22em] uppercase hover:text-[#C7A36A] transition-colors">View all models →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {featured.map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="group block">
                <div className="relative aspect-[3/4] overflow-hidden rounded-xl bg-[#F5F5F5]">
                  <img src={p.hero} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-[1.045]" />
                </div>
                <div className="mt-5 flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.24em] uppercase text-black/45">{p.family === 'capsule' ? 'Capsule Suites' : 'Studio Suites'}</div>
                    <div className="font-display text-2xl mt-1 tracking-tight">{p.name}</div>
                  </div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-black/50">{p.specs.carpetArea}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* ============ CHAPTER BREAK — B ============ */}
      <ChapterBreak
        src="https://customer-assets-gfyr7b9c.emergentagent.net/job_modularoom-design/artifacts/fmkls9lj_Gemini%20SS26%208%281%29.webp"
        chapter="Chapter III"
        title={<>Engineered<br/><span className="italic text-white/40">to product tolerances.</span></>}
        subtitle="Precision, structural integrity, materials, insulation, transport, installation and warranty, presented as an engineering spec sheet."
        minHeight="70svh"
      />

      {/* Slim deep-story teaser → Engineering page */}
      <section className="bg-[#111] text-white py-32 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Engineering</div>
            <h2 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.98]">Built to product-grade tolerances.<br/><span className="italic text-white/40">Delivered to hospitality standards.</span></h2>
            <p className="mt-8 max-w-lg text-[15px] text-white/70 leading-relaxed">Precision, structural integrity, materials, insulation, transportability, installation and warranty, presented as an engineering spec sheet.</p>
          </div>
          <div className="md:col-span-4 md:justify-self-end flex flex-col gap-3">
            <a href="/engineering" className="inline-flex items-center justify-between gap-2 rounded-full bg-white text-[#111] px-7 py-3.5 text-[12px] tracking-[0.2em] uppercase hover:bg-[#C7A36A] hover:text-white transition-colors">Read the specs →</a>
            <a href="/materials" className="inline-flex items-center justify-between gap-2 rounded-full border border-white/30 px-7 py-3.5 text-[12px] tracking-[0.2em] uppercase hover:bg-white hover:text-[#111] transition-colors">Materials & finishes →</a>
          </div>
        </div>
      </section>

      {/* ============ 09 · FINAL CTA ============ */}
      <section className="relative overflow-hidden bg-[#111] text-white py-40 md:py-56">
        <AtmoBg src={ATMOSPHERE.brandDark} base="black" opacity={0.20} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16 items-end">
          <div className="md:col-span-8">
            <SectionLabel n="08" tone="light">Start your project</SectionLabel>
            <h2 className="mt-10 font-display text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.98]">
              Your next space<br/><span className="italic text-white/40">begins with a conversation.</span>
            </h2>
            <p className="mt-8 max-w-lg text-[15px] text-white/70 leading-relaxed">
              Share your site, timeline and vision. Our team will respond with a curated proposal, usually within one business day.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact#book" data-testid="cta-book" className="inline-flex items-center gap-2 rounded-full bg-white text-[#111] px-9 py-4 text-[13px] tracking-[0.2em] uppercase hover:bg-[#C7A36A] hover:text-white transition-colors duration-500">
              Book Consultation <ArrowRight size={16}/>
            </Link>
            <div className="mt-6 text-[11px] tracking-[0.28em] uppercase text-white/45">
              modularoom.info@gmail.com<br/>+91 98750 67172
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
